import type { FC } from "react";
import { useState, useEffect } from "react";

import { UIView, useCurrentStateAndParams } from "@uirouter/react";
import { startsWith } from "lodash";
import { ErrorBoundary } from "react-error-boundary";
import styled from "styled-components";

import Sentry from "@similarweb/sentry-logger";
import { colorsPalettes } from "@similarweb/styles";
import { Dropdown, EllipsisDropdownItem, IconButton, PlainTooltip } from "@similarweb/ui-components";

import type { QueryBarItemMode } from "app/components/compare/KeywordsQueryBar/KeywordsQueryBarTypes";
import { ErrorFallback } from "app/components/error-boundary-no-data/ErrorBoundaryEmptyState";
import { KeywordAnalysisSubNav } from "app/pages/keyword-research/keyword-analysis/common-components/KeywordAnalysisSubNav";
import useStickySubNav from "app/components/sub-navigation-bar/UseStickySubNav";
import { translate } from "app/filters/i18nFilter";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import type { IRouterState } from "single-spa/react-app/router/IRouterState";
import { DEFAULT_PAGE_LAYOUT_CLASS_NAME } from "app/UtilitiesAndConstants/Constants/css";

import { getGroupById } from "../../common-components/KeywordAnalysisModuleFrame";
import { KEYWORD_GROUP_PREFIX, keywordGroupsQueryClient, keywordGroupsTransforms } from "app/services/keyword-groups";

const loadKeywordsGroups = async () => {
    const { userGroups } = await keywordGroupsQueryClient.getAllGroups();
    return keywordGroupsTransforms.toDropDown(userGroups).map((item) => {
        return {
            ...item,
            shareable: true,
            editable: true,
            deletable: true,
        };
    });
};

const loadSharedKeywordGroups = async () => {
    const { sharedGroups } = await keywordGroupsQueryClient.getAllGroups();
    return keywordGroupsTransforms.toDropDown(sharedGroups).map((item) => {
        return {
            ...item,
            shareable: false,
            editable: false,
            deletable: false,
        };
    });
};

export type EngineName = "google" | "youtube";
interface IEngine {
    id: EngineName;
    displayName: string;
    description: string;
}

const engines: Record<EngineName, IEngine> = {
    google: {
        id: "google",
        get displayName() {
            return translate("digital.marketing.keywords.research.google.search.displayName");
        },
        get description() {
            return translate("digital.marketing.keywords.research.google.search.description");
        },
    },
    youtube: {
        id: "youtube",
        get displayName() {
            return translate("digital.marketing.keywords.research.youtube.search.displayName");
        },
        get description() {
            return translate("digital.marketing.keywords.research.youtube.search.description");
        },
    },
};

const IconButtonStyled = styled(IconButton)`
    .SWReactIcons {
        align-items: center;
        justify-content: center;
    }
`;

const GeneratorTypeDropdownContainer = styled.div`
    margin-left: 12px;
`;

interface Params {
    keyword: string;
    searchEngine: EngineName;
}

interface StateParams {
    params: Params;
    state: IRouterState;
}

export const KeywordGeneratorModuleFrame: FC = () => {
    const { params, state }: StateParams = useCurrentStateAndParams();
    const [shouldShowTooltip, setShouldShowTooltip] = useState(true);
    const { SubNavWrapper, PageContentWrapper, subNavWrapperProps, pageContentWrapperProps } = useStickySubNav();
    const searchTerm = params.keyword || "";
    const currentSearchEngine: EngineName = params.searchEngine;

    const [keywordsGroupsMyListsForDropDown, setKeywordsGroupsMyListsForDropDown] = useState([]);
    const [keywordsGroupsSharedListsForDropDown, setKeywordsGroupsSharedListsForDropDown] = useState([]);

    const handleSearchEngineChange = (id: EngineName) => {
        if (id !== currentSearchEngine) {
            proNavigator.updateQueryParams({ searchEngine: id });
        }
    };

    useEffect(() => {
        const loadKeywordGroupsData = async () => {
            try {
                const [userGroups, sharedGroups] = await Promise.all([loadKeywordsGroups(), loadSharedKeywordGroups()]);
                setKeywordsGroupsMyListsForDropDown(userGroups);
                setKeywordsGroupsSharedListsForDropDown(sharedGroups);
            } catch (error) {
                Sentry.error("Error loading keyword groups:", error);
                setKeywordsGroupsMyListsForDropDown([]);
                setKeywordsGroupsSharedListsForDropDown([]);
            }
        };

        void loadKeywordGroupsData();
    }, []);

    const keywordsGroupsForDropDown = [];
    keywordsGroupsForDropDown.push(keywordsGroupsMyListsForDropDown);
    keywordsGroupsForDropDown.push(keywordsGroupsSharedListsForDropDown);
    const mode: QueryBarItemMode = !startsWith(searchTerm, KEYWORD_GROUP_PREFIX) ? "keyword" : "keywordGroup";

    const selectedKeywordsGroupId =
        mode === "keywordGroup" ? getGroupById(searchTerm, keywordsGroupsForDropDown)?.id : undefined;

    const pageLayoutClassName = state.pageLayoutClassName ?? DEFAULT_PAGE_LAYOUT_CLASS_NAME;
    return (
        <ErrorBoundary
            FallbackComponent={(props) => <ErrorFallback {...props} />}
            resetKeys={[state, params]}
        >
            <section
                className="keyword-analysis-search sw-layout-no-scroll-container"
                style={{ height: "100%", display: "flex" }}
            >
                <span
                    className="u-flex-row u-flex-column"
                    style={{ flexGrow: 1, overflowX: "hidden", width: "100%" }}
                >
                    <SubNavWrapper
                        className="sw-layout-subnav"
                        {...subNavWrapperProps}
                    >
                        <KeywordAnalysisSubNav
                            categories={[]}
                            customCategories={keywordsGroupsForDropDown}
                            selectedCat={selectedKeywordsGroupId}
                            trackName={"Header/Keyword Group Filter"}
                            trackContext={"keyword group"}
                            removeItemTooltip={{
                                title: "keyword.generator.frame.remove.item.tooltip.title",
                                buttonOk: "keyword.generator.frame.remove.item.tooltip.buttonOk",
                                buttonCancel: "keyword.generator.frame.remove.item.tooltip.buttonCancel",
                            }}
                            placeholderItem={{
                                text: "keyword.generator.frame.placeholder.item.text",
                                icon: "sw-icon-folder",
                            }}
                            mode={mode}
                            hideKeywordGroups={currentSearchEngine !== "google"}
                            searchTerm={searchTerm}
                            showAutoCreateListCTA={false}
                            isLoading={false}
                            showCompareButton={state.showCompareButton}
                            topLeftExtraComponent={
                                <GeneratorTypeDropdownContainer>
                                    <PlainTooltip
                                        placement="bottom"
                                        enabled={shouldShowTooltip}
                                        tooltipContent={translate(
                                            "keyword.generator.frame.search.engine.switcher.toolTip.text",
                                        )}
                                    >
                                        <span>
                                            <Dropdown
                                                dropdownPopupHeight={240}
                                                width={207}
                                                onClick={({ id }) => handleSearchEngineChange(id as EngineName)}
                                                onToggle={(isOpen) => setShouldShowTooltip(!isOpen)}
                                                cssClassContainer="DropdownContent-container Popup-content--pro"
                                                dropdownPopupPlacement={"bottom-left"}
                                                buttonWidth="fit-content"
                                            >
                                                {[
                                                    <IconButtonStyled
                                                        key="button"
                                                        type="outlined"
                                                        iconName="arrow"
                                                        iconSize="10px"
                                                        placement="right"
                                                    >
                                                        {engines[currentSearchEngine].displayName}
                                                    </IconButtonStyled>,
                                                    ...Object.values(engines).map((engine) => (
                                                        <EllipsisDropdownItem
                                                            id={engine.id}
                                                            key={engine.id}
                                                            selected={currentSearchEngine === engine.id}
                                                            iconName={engine.id}
                                                            iconSize="sm"
                                                            iconColor={colorsPalettes.carbon[200]}
                                                            tooltipText={engine.description}
                                                            tooltipPlacement="right"
                                                        >
                                                            {engine.displayName}
                                                        </EllipsisDropdownItem>
                                                    )),
                                                ]}
                                            </Dropdown>
                                        </span>
                                    </PlainTooltip>
                                </GeneratorTypeDropdownContainer>
                            }
                        />
                    </SubNavWrapper>
                    <PageContentWrapper
                        className="sw-layout-research sw-layout-scrollable-element use-sticky-css-rendering"
                        {...pageContentWrapperProps}
                    >
                        <div className={`sw-page-websiteAnalysis ${pageLayoutClassName}`}>
                            <ErrorBoundary
                                FallbackComponent={(props) => <ErrorFallback {...props} />}
                                resetKeys={[state, params]}
                            >
                                <UIView />
                            </ErrorBoundary>
                        </div>
                    </PageContentWrapper>
                </span>
            </section>
        </ErrorBoundary>
    );
};
